/**
 * Selection, spatial indexing, hit-testing, and lasso intersection for Paper2Ink.
 * Guarantees proper handling of compound holes, transforms, tiny punctuation dots,
 * and edge-intersecting lasso selection.
 */

import type { CIMDocument, CompoundFill, CenterlineStroke, InkObject } from './model.ts';

export type Point2D = [number, number];
export type BoundingBox = [number, number, number, number]; // [minX, minY, maxX, maxY]

export function applyAffine(t: readonly number[], pt: Point2D): Point2D {
  const [a, b, c, d, e, f] = t;
  return [a * pt[0] + c * pt[1] + e, b * pt[0] + d * pt[1] + f];
}

export function inverseAffine(t: readonly number[]): [number, number, number, number, number, number] {
  const [a, b, c, d, e, f] = t;
  const det = a * d - b * c;
  if (Math.abs(det) < 1e-12) {
    throw new Error('Singular transform cannot be inverted');
  }
  return [
    d / det,
    -b / det,
    -c / det,
    a / det,
    (c * f - d * e) / det,
    (b * e - a * f) / det,
  ];
}

export function isPointInPolygon(pt: Point2D, ring: readonly Point2D[]): boolean {
  const [x, y] = pt;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

export function distToSegmentSquared(p: Point2D, v: Point2D, w: Point2D): number {
  const l2 = (v[0] - w[0]) ** 2 + (v[1] - w[1]) ** 2;
  if (l2 === 0) return (p[0] - v[0]) ** 2 + (p[1] - v[1]) ** 2;
  let t = ((p[0] - v[0]) * (w[0] - v[0]) + (p[1] - v[1]) * (w[1] - v[1])) / l2;
  t = Math.max(0, Math.min(1, t));
  const projX = v[0] + t * (w[0] - v[0]);
  const projY = v[1] + t * (w[1] - v[1]);
  return (p[0] - projX) ** 2 + (p[1] - projY) ** 2;
}

export function segmentsIntersect(a: Point2D, b: Point2D, c: Point2D, d: Point2D): boolean {
  function ccw(p1: Point2D, p2: Point2D, p3: Point2D): boolean {
    return (p3[1] - p1[1]) * (p2[0] - p1[0]) > (p2[1] - p1[1]) * (p3[0] - p1[0]);
  }
  return ccw(a, c, d) !== ccw(b, c, d) && ccw(a, b, c) !== ccw(a, b, d);
}

export function computeObjectBounds(obj: InkObject): BoundingBox {
  const cached = BOUNDS_CACHE.get(obj);
  if (cached) return cached;
  const bounds = computeObjectBoundsUncached(obj);
  BOUNDS_CACHE.set(obj, bounds);
  return bounds;
}

/**
 * Bounds cache keyed by object identity.
 *
 * Objects are treated as immutable everywhere (commands replace objects, never
 * mutate them), so a WeakMap keyed on the object itself is safe and lets the
 * marquee, selection highlight, and context bar share one traversal instead of
 * re-walking every ring/point of a 10k-object scene on each interaction.
 * Cloned/replaced objects get fresh identities and therefore fresh bounds.
 */
const BOUNDS_CACHE: WeakMap<InkObject, BoundingBox> = new WeakMap();

function computeObjectBoundsUncached(obj: InkObject): BoundingBox {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  function include(pt: Point2D) {
    const pagePt = applyAffine(obj.transform, pt);
    if (pagePt[0] < minX) minX = pagePt[0];
    if (pagePt[1] < minY) minY = pagePt[1];
    if (pagePt[0] > maxX) maxX = pagePt[0];
    if (pagePt[1] > maxY) maxY = pagePt[1];
  }

  if (obj.kind === 'compound_fill') {
    for (const ring of obj.rings) {
      for (const pt of ring.points) {
        include(pt);
      }
    }
  } else if (obj.kind === 'centerline_stroke') {
    const pad = (obj.width || 1) / 2;
    for (const pt of obj.points) {
      include(pt);
    }
    minX -= pad;
    minY -= pad;
    maxX += pad;
    maxY += pad;
  }

  return [minX, minY, maxX, maxY];
}

export function boxesIntersect(b1: BoundingBox, b2: BoundingBox): boolean {
  return !(b1[2] < b2[0] || b1[0] > b2[2] || b1[3] < b2[1] || b1[1] > b2[3]);
}

export function hitTestPoint(
  doc: CIMDocument,
  pagePoint: Point2D,
  tolerance: number = 3.0
): string | null {
  // Hit-test in reverse render order (topmost first)
  for (let i = doc.objects.length - 1; i >= 0; i--) {
    if (objectHitAt(doc.objects[i], pagePoint, tolerance)) {
      return doc.objects[i].id;
    }
  }
  return null;
}

/**
 * Every object whose geometry is within `radius` of the point.
 *
 * The eraser needs all hits, not just the topmost one: dragging across a
 * cluster of strokes must clear the whole cluster in a single gesture.
 */
export function hitTestRadius(
  doc: CIMDocument,
  pagePoint: Point2D,
  radius: number
): string[] {
  const hits: string[] = [];
  for (const obj of doc.objects) {
    if (objectHitAt(obj, pagePoint, radius)) {
      hits.push(obj.id);
    }
  }
  return hits;
}

function objectHitAt(obj: InkObject, pagePoint: Point2D, tolerance: number): boolean {
  const bounds = computeObjectBounds(obj);
  if (
    pagePoint[0] < bounds[0] - tolerance ||
    pagePoint[0] > bounds[2] + tolerance ||
    pagePoint[1] < bounds[1] - tolerance ||
    pagePoint[1] > bounds[3] + tolerance
  ) {
    return false;
  }

  const localPt = applyAffine(inverseAffine(obj.transform), pagePoint);

  if (obj.kind === 'compound_fill') {
    let containingRings = 0;
    let minDistSq = Infinity;

    for (const ring of obj.rings) {
      if (isPointInPolygon(localPt, ring.points)) {
        containingRings++;
      }
      for (let j = 0, k = ring.points.length - 1; j < ring.points.length; k = j++) {
        const dSq = distToSegmentSquared(localPt, ring.points[k], ring.points[j]);
        if (dSq < minDistSq) minDistSq = dSq;
      }
    }

    // Even-odd rule: odd count means inside solid fill
    if (containingRings % 2 === 1) {
      return true;
    }
    // Punctuation dot tolerance: if close to boundary of tiny mark
    if (minDistSq <= tolerance ** 2) {
      return true;
    }
    return false;
  }

  if (obj.kind === 'centerline_stroke') {
    const allowedDist = (obj.width / 2 + tolerance) ** 2;
    for (let j = 0; j < obj.points.length - 1; j++) {
      if (distToSegmentSquared(localPt, obj.points[j], obj.points[j + 1]) <= allowedDist) {
        return true;
      }
    }
    return false;
  }

  return false;
}

export function simplifyLassoPolygon(points: readonly Point2D[], tolerance = 1): Point2D[] {
  if (points.length < 8 || tolerance <= 0) return [...points];
  // A closed loop has no natural endpoints. Split at the vertex farthest
  // from the first point, then simplify both open arcs so the closure stays
  // faithful rather than collapsing to a line.
  let split = 1;
  let maxDist = -1;
  for (let i = 1; i < points.length; i++) {
    const dx = points[i][0] - points[0][0];
    const dy = points[i][1] - points[0][1];
    const d = dx * dx + dy * dy;
    if (d > maxDist) { maxDist = d; split = i; }
  }
  const simplifyArc = (arc: Point2D[]): Point2D[] => {
    const keep = new Uint8Array(arc.length);
    keep[0] = keep[arc.length - 1] = 1;
    const stack: Array<[number, number]> = [[0, arc.length - 1]];
    const tolSq = tolerance * tolerance;
    while (stack.length) {
      const [start, end] = stack.pop()!;
      let farthest = -1;
      let distance = tolSq;
      for (let i = start + 1; i < end; i++) {
        const d = distToSegmentSquared(arc[i], arc[start], arc[end]);
        if (d > distance) { distance = d; farthest = i; }
      }
      if (farthest >= 0) {
        keep[farthest] = 1;
        stack.push([start, farthest], [farthest, end]);
      }
    }
    return arc.filter((_, i) => keep[i] === 1);
  };
  const first = simplifyArc(points.slice(0, split + 1));
  const second = simplifyArc([...points.slice(split), points[0]]);
  second.pop(); // duplicate closing vertex
  const result = [...first, ...second.slice(1)];
  return result.length >= 3 ? result : [...points];
}

export function selectByLasso(
  doc: CIMDocument,
  lassoPolygon: Point2D[]
): string[] {
  if (lassoPolygon.length < 3) return [];

  // Compute lasso bounds
  let lMinX = Infinity, lMinY = Infinity, lMaxX = -Infinity, lMaxY = -Infinity;
  for (const pt of lassoPolygon) {
    if (pt[0] < lMinX) lMinX = pt[0];
    if (pt[1] < lMinY) lMinY = pt[1];
    if (pt[0] > lMaxX) lMaxX = pt[0];
    if (pt[1] > lMaxY) lMaxY = pt[1];
  }
  const lassoBounds: BoundingBox = [lMinX, lMinY, lMaxX, lMaxY];

  const selectedIds: string[] = [];

  for (const obj of doc.objects) {
    const objBounds = computeObjectBounds(obj);
    if (!boxesIntersect(lassoBounds, objBounds)) {
      continue;
    }

    let isSelected = false;

    if (obj.kind === 'compound_fill') {
      // 1. Check if any object vertex is inside lasso
      for (const ring of obj.rings) {
        for (const pt of ring.points) {
          const pagePt = applyAffine(obj.transform, pt);
          if (isPointInPolygon(pagePt, lassoPolygon)) {
            isSelected = true;
            break;
          }
        }
        if (isSelected) break;
      }

      // 2. Check if any lasso vertex is inside object
      if (!isSelected) {
        for (const lp of lassoPolygon) {
          const localPt = applyAffine(inverseAffine(obj.transform), lp);
          let count = 0;
          for (const ring of obj.rings) {
            if (isPointInPolygon(localPt, ring.points)) count++;
          }
          if (count % 2 === 1) {
            isSelected = true;
            break;
          }
        }
      }

      // 3. Check if any object edge intersects any lasso edge
      if (!isSelected) {
        outerLoop: for (const ring of obj.rings) {
          for (let j = 0, k = ring.points.length - 1; j < ring.points.length; k = j++) {
            const op1 = applyAffine(obj.transform, ring.points[k]);
            const op2 = applyAffine(obj.transform, ring.points[j]);
            for (let li = 0, lj = lassoPolygon.length - 1; li < lassoPolygon.length; lj = li++) {
              if (segmentsIntersect(op1, op2, lassoPolygon[lj], lassoPolygon[li])) {
                isSelected = true;
                break outerLoop;
              }
            }
          }
        }
      }
    } else if (obj.kind === 'centerline_stroke') {
      for (let j = 0; j < obj.points.length; j++) {
        const pagePt = applyAffine(obj.transform, obj.points[j]);
        if (isPointInPolygon(pagePt, lassoPolygon)) {
          isSelected = true;
          break;
        }
      }

      if (!isSelected) {
        strokeLoop: for (let j = 0; j < obj.points.length - 1; j++) {
          const sp1 = applyAffine(obj.transform, obj.points[j]);
          const sp2 = applyAffine(obj.transform, obj.points[j + 1]);
          for (let li = 0, lj = lassoPolygon.length - 1; li < lassoPolygon.length; lj = li++) {
            if (segmentsIntersect(sp1, sp2, lassoPolygon[lj], lassoPolygon[li])) {
              isSelected = true;
              break strokeLoop;
            }
          }
        }
      }
    }

    if (isSelected) {
      selectedIds.push(obj.id);
    }
  }

  return selectedIds;
}

export function getGroupMembers(doc: CIMDocument, groupId: string): string[] {
  return doc.objects.filter((o) => o.group_id === groupId).map((o) => o.id);
}

/**
 * Windows-style rectangular marquee selection.
 *
 * Selects every object whose world-space bounds intersect the rectangle.
 * Intersection (not containment) is deliberate: a stroke drawn across the
 * box edge is part of the region the user framed, so it must travel with
 * the rest of the selection.
 */
export function selectByMarquee(doc: CIMDocument, rect: BoundingBox): string[] {
  const selectedIds: string[] = [];
  for (const obj of doc.objects) {
    if (boxesIntersect(rect, computeObjectBounds(obj))) {
      selectedIds.push(obj.id);
    }
  }
  return selectedIds;
}
